import { createFileRoute, Link } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import {
  Users,
  Building2,
  Activity,
  FileText,
  ArrowRight,
  CheckCircle2,
  XCircle,
  AlertCircle,
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { api, endpoints } from '@/lib/api-client';

export const Route = createFileRoute('/_dashboard/admin/superadmin/')({
  component: SuperAdminDashboard,
});

interface SystemHealth {
  status: 'healthy' | 'degraded' | 'unhealthy';
  timestamp: string;
  services: Record<string, { status: string; latency?: number; error?: string }>;
}

interface UserStats {
  totalUsers: number;
  byRole: Record<string, number>;
  superAdminCount: number;
  recentUsers: number;
}

interface AuditSummary {
  totalEvents: number;
  byAction: Record<string, number>;
  recentActivity: Array<{
    id: string;
    action: string;
    userEmail?: string;
    createdAt: string;
  }>;
}

function SuperAdminDashboard() {
  const [health, setHealth] = useState<SystemHealth | null>(null);
  const [userStats, setUserStats] = useState<UserStats | null>(null);
  const [auditSummary, setAuditSummary] = useState<AuditSummary | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [healthData, statsData, auditData] = await Promise.all([
          api.get<SystemHealth>(endpoints.adminSystemHealth),
          api.get<UserStats>(endpoints.adminUsersStats),
          api.get<AuditSummary>(endpoints.adminAuditSummary),
        ]);
        setHealth(healthData);
        setUserStats(statsData);
        setAuditSummary(auditData);
      } catch (error) {
        console.error('Failed to fetch dashboard data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
    // Refresh every 30 seconds
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, []);

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div>
          <Skeleton className="h-8 w-48 mb-2" />
          <Skeleton className="h-4 w-64" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-32" />
          ))}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Skeleton className="h-64" />
          <Skeleton className="h-64" />
        </div>
      </div>
    );
  }

  const healthyServices = health
    ? Object.values(health.services).filter((s) => s.status === 'healthy').length
    : 0;
  const totalServices = health ? Object.keys(health.services).length : 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold">Platform Admin Dashboard</h1>
        <p className="text-muted-foreground">
          Monitor system health and manage platform resources
        </p>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">System Status</CardTitle>
            <Activity className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              {health?.status === 'healthy' ? (
                <CheckCircle2 className="h-5 w-5 text-green-500" />
              ) : health?.status === 'degraded' ? (
                <AlertCircle className="h-5 w-5 text-yellow-500" />
              ) : (
                <XCircle className="h-5 w-5 text-red-500" />
              )}
              <span className="text-2xl font-bold capitalize">{health?.status || 'Unknown'}</span>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {healthyServices}/{totalServices} services healthy
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Total Users</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{userStats?.totalUsers || 0}</div>
            <p className="text-xs text-muted-foreground mt-1">
              +{userStats?.recentUsers || 0} this week
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Super Admins</CardTitle>
            <Building2 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{userStats?.superAdminCount || 0}</div>
            <p className="text-xs text-muted-foreground mt-1">Platform administrators</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Audit Events</CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{auditSummary?.totalEvents || 0}</div>
            <p className="text-xs text-muted-foreground mt-1">Total logged events</p>
          </CardContent>
        </Card>
      </div>

      {/* Service Health & Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Service Health */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Service Health</CardTitle>
                <CardDescription>Real-time status of platform services</CardDescription>
              </div>
              <Button variant="ghost" size="sm" asChild>
                <Link to="/admin/superadmin/system">
                  View Details <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {health?.services &&
                Object.entries(health.services).map(([name, service]) => (
                  <div key={name} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {service.status === 'healthy' ? (
                        <CheckCircle2 className="h-4 w-4 text-green-500" />
                      ) : (
                        <XCircle className="h-4 w-4 text-red-500" />
                      )}
                      <span className="font-medium capitalize">{name}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      {service.latency && (
                        <span className="text-xs text-muted-foreground">
                          {service.latency}ms
                        </span>
                      )}
                      <Badge variant={service.status === 'healthy' ? 'default' : 'destructive'}>
                        {service.status}
                      </Badge>
                    </div>
                  </div>
                ))}
            </div>
          </CardContent>
        </Card>

        {/* Recent Activity */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Recent Activity</CardTitle>
                <CardDescription>Latest audit log entries</CardDescription>
              </div>
              <Button variant="ghost" size="sm" asChild>
                <Link to="/admin/audit">
                  View All <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {auditSummary?.recentActivity?.slice(0, 5).map((event) => (
                <div key={event.id} className="flex items-center justify-between text-sm">
                  <div className="flex flex-col">
                    <span className="font-medium">{event.action}</span>
                    <span className="text-xs text-muted-foreground">
                      {event.userEmail || 'Unknown user'}
                    </span>
                  </div>
                  <span className="text-xs text-muted-foreground">
                    {new Date(event.createdAt).toLocaleString()}
                  </span>
                </div>
              ))}
              {(!auditSummary?.recentActivity || auditSummary.recentActivity.length === 0) && (
                <p className="text-sm text-muted-foreground text-center py-4">
                  No recent activity
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <Card>
        <CardHeader>
          <CardTitle>Quick Actions</CardTitle>
          <CardDescription>Common administrative tasks</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" asChild>
              <Link to="/admin/superadmin/users">
                <Users className="mr-2 h-4 w-4" />
                Manage Users
              </Link>
            </Button>
            <Button variant="outline" asChild>
              <Link to="/admin/superadmin/organizations">
                <Building2 className="mr-2 h-4 w-4" />
                Manage Organizations
              </Link>
            </Button>
            <Button variant="outline" asChild>
              <Link to="/admin/audit">
                <FileText className="mr-2 h-4 w-4" />
                View Audit Logs
              </Link>
            </Button>
            <Button variant="outline" asChild>
              <Link to="/admin/data/postgres">
                <Activity className="mr-2 h-4 w-4" />
                Database Viewer
              </Link>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
